
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import { Recommended } from '../../../components/Recommended/Recommended';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "step-1-display-button"
    }}>{`Step 1: Display button`}</h1>
    <p>{`We strongly recommend that your software models its apply form in the following manner:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`If the `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter is present,
skip ahead to handle authorization in the `}<a parentName="p" {...{
            "href": "/use-cases/apply-with-seek/handle-authorization#allow-access"
          }}>{`Allow access`}</a>{` scenario.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`If the `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter is not present,
display the Apply with SEEK button with a `}<inlineCode parentName="p">{`redirectUri`}</inlineCode>{` back to the current page.`}</p>
      </li>
    </ul>
    <p>{`This simplified model enables your software to seamlessly handle all scenarios described below with minimal effort.`}</p>
    <img alt="" data-scoobie-style="none" height="424" src={require('../../../../mermaid/.38e79c68021324499bc4d9bc30234fcb21856a38.mmd.svg')} title="" width="438" />
    <h2 {...{
      "id": "pre-fill-scenarios"
    }}>{`Pre-fill scenarios`}</h2>
    <p>{`Apply with SEEK can be used by a candidate to pre-fill in three scenarios:`}</p>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`When a candidate links out from a SEEK job ad to your apply form,
and they manually click the Apply with SEEK button on the form.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate links out from a SEEK job ad to your apply form,
and they have pre-authorized your software to pre-fill the form.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`When a candidate arrives on your apply form directly or through another channel,
and they manually click the Apply with SEEK button on the form.`}</p>
      </li>
    </ol>
    <h3 {...{
      "id": "scenario-1-candidate-arrives-from-seek-without-pre-authorization"
    }}>{`Scenario 1: Candidate arrives from SEEK without pre-authorization`}</h3>
    <img alt="" data-scoobie-style="none" height="414" src={require('../../../../mermaid/.1a8104b5be861b47ae94fa1b9df755e62a3d90d9.mmd.svg')} title="Full sequence" width="1167" />
    <p>{`If the candidate has `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`linked out`}</a>{` from a SEEK job ad,
you will receive a `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter appended to the query string of the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplicationMethod/field/applicationUri"
      }}><inlineCode parentName="a">{`applicationUri`}</inlineCode></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text"
      }}>{`https://example.com/position/123/?seek-token=eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9
`}</code></pre>
    <p>{`Propagate the token to the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` and subsequent `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/send-application-complete-signal"
      }}>{`signals`}</a>{` like apply form submission.
It is unique to that specific candidate, job ad, and session,
and must not be reused outside of the context of their current application.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":2982},\"end\":{\"line\":89,\"column\":4,\"offset\":3141},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":91,\"column\":1,\"offset\":3143},\"end\":{\"line\":106,\"column\":4,\"offset\":3798},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":108,\"column\":1,\"offset\":3800},\"end\":{\"line\":118,\"column\":4,\"offset\":4084},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":79,\"column\":1,\"offset\":2982},\"end\":{\"line\":89,\"column\":4,\"offset\":3141},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://example.com/position/123/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":91,\"column\":1,\"offset\":3143},\"end\":{\"line\":106,\"column\":4,\"offset\":3798},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":108,\"column\":1,\"offset\":3800},\"end\":{\"line\":118,\"column\":4,\"offset\":4084},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <h3 {...{
      "id": "scenario-2-candidate-arrives-from-seek-with-pre-authorization"
    }}>{`Scenario 2: Candidate arrives from SEEK with pre-authorization`}</h3>
    <img alt="" data-scoobie-style="none" height="414" src={require('../../../../mermaid/.1d4db9b654ab1feec2a5b5ee5f06e8aeb64ddede.mmd.svg')} title="Full sequence" width="991" />
    <p>{`If your apply page receives a `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter in the query string,
skip ahead to handle authorization in the `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/handle-authorization#allow-access"
      }}>{`Allow access`}</a>{` scenario.`}</p>
    <p>{`This is a variation of Scenario 1 where the candidate has `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`linked out`}</a>{` from a SEEK job ad,
but they have additionally pre-authorized your software to pre-fill the form using their SEEK Profile.
You may test this scenario by posting a job ad using your `}<a parentName="p" {...{
        "href": "/introduction#integration-process"
      }}>{`test hirer account`}</a>{` with a position title that contains the string `}<inlineCode parentName="p">{`[PREAUTH]`}</inlineCode>{`.`}</p>
    <p>{`As with Scenario 1,
propagate the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter to subsequent `}<a parentName="p" {...{
        "href": "/use-cases/apply-with-seek/send-application-complete-signal"
      }}>{`signals`}</a>{` like apply form submission.`}</p>
    <h3 {...{
      "id": "scenario-3-candidate-arrives-from-another-source"
    }}>{`Scenario 3: Candidate arrives from another source`}</h3>
    <img alt="" data-scoobie-style="none" height="318" src={require('../../../../mermaid/.ed82c3aa864bb663d2beb63a927739df62384539.mmd.svg')} title="Full sequence" width="956" />
    <p>{`Where a candidate has arrived at your apply form from another source
(direct link, company career site, etc.),
the `}<inlineCode parentName="p">{`seek-token`}</inlineCode>{` parameter will not be present in the query string and should be omitted from the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":166,\"column\":1,\"offset\":5817},\"end\":{\"line\":176,\"column\":4,\"offset\":5976},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The HTTPS URL of your apply form for the specific position.\\n    \\\"applicationUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // An optional hirer-provided opaque job reference.\\n    \\\"seekHirerJobReference\\\": \\\"REQ-65558\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":178,\"column\":1,\"offset\":5978},\"end\":{\"line\":196,\"column\":4,\"offset\":6693},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":198,\"column\":1,\"offset\":6695},\"end\":{\"line\":208,\"column\":4,\"offset\":6979},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":166,\"column\":1,\"offset\":5817},\"end\":{\"line\":176,\"column\":4,\"offset\":5976},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":\"default\",\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://example.com/position/123/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "your": true,
        "apply": true,
        "form": true,
        "specific": true,
        "\\\"applicationUri\\\":": true,
        "An": true,
        "optional": true,
        "hirer-provided": true,
        "opaque": true,
        "job": true,
        "reference.\\n": true,
        "\\\"seekHirerJobReference\\\":": true,
        "\\\"REQ-65558\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":178,\"column\":1,\"offset\":5978},\"end\":{\"line\":196,\"column\":4,\"offset\":6693},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":198,\"column\":1,\"offset\":6695},\"end\":{\"line\":208,\"column\":4,\"offset\":6979},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <h2 {...{
      "id": "render-the-button"
    }}>{`Render the button`}</h2>
    <p>{`Call the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{` each time your apply form is loaded without a `}<inlineCode parentName="p">{`seek-prefill-id`}</inlineCode>{` parameter in the query string.
It provides display assets for the Apply with SEEK button and a single-use authorization URL to link to.
The authorization page prompts the candidate to share their SEEK Profile for the purpose of pre-filling your apply form.`}</p>

    <h3 {...{
      "id": "option-1-seek-button-recommended-"
    }}>{`Option 1: SEEK button `}<Recommended mdxType="Recommended" /></h3>
    <p>{`We recommend this option as it presents consistent and recognisable branding to the candidate while reducing ongoing maintenance effort.`}</p>
    <p>{`Take SEEK’s branded button by selecting one of the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/images"
      }}><inlineCode parentName="a">{`images`}</inlineCode></a>{`.
You should use `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/buttonLabel"
      }}><inlineCode parentName="a">{`buttonLabel`}</inlineCode></a>{` as an accessible `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role#basic_buttons"
      }}><inlineCode parentName="a">{`aria-label`}</inlineCode></a>{` or `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/alt#usage_notes"
      }}><inlineCode parentName="a">{`alt`}</inlineCode></a>{`,
and link the button to the supplied `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/url"
      }}><inlineCode parentName="a">{`url`}</inlineCode></a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    images {\\n      accent\\n    }\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":232,\"column\":1,\"offset\":8310},\"end\":{\"line\":242,\"column\":4,\"offset\":8469},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"<Partner Name> on behalf of <Hirer Name>\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":244,\"column\":1,\"offset\":8471},\"end\":{\"line\":259,\"column\":4,\"offset\":9122},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"images\\\": {\\n      \\\"accent\\\": \\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n    },\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":261,\"column\":1,\"offset\":9124},\"end\":{\"line\":271,\"column\":4,\"offset\":9408},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "images": true,
        "accent\\n": true,
        "}\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":232,\"column\":1,\"offset\":8310},\"end\":{\"line\":242,\"column\":4,\"offset\":8469},\"indent\":[1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://example.com/position/123/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"<Partner": true,
        "Name>": true,
        "on": true,
        "behalf": true,
        "of": true,
        "<Hirer": true,
        "Name>\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":244,\"column\":1,\"offset\":8471},\"end\":{\"line\":259,\"column\":4,\"offset\":9122},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"images\\\":": true,
        "\\\"accent\\\":": true,
        "\\\"https://integration.seek.com/awsk/button-images/seek/en/accent.svg\\\"\\n": true,
        "},\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":261,\"column\":1,\"offset\":9124},\"end\":{\"line\":271,\"column\":4,\"offset\":9408},\"indent\":[1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    images {
      accent
    }
    url
  }
}
`}</code></pre>
    <p>{`Here are a few samples using `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Learn/Tools_and_testing/Client-side_JavaScript_frameworks/React_getting_started"
      }}>{`JSX`}</a>{` pseudocode:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label=\\\"form + input\\\"\",\"value\":\"<form action={applyWithSeekButton.url} method=\\\"GET\\\">\\n  <input\\n    style={{ background: applyWithSeekButton.images.accent }}\\n    type=\\\"submit\\\"\\n    value={applyWithSeekButton.buttonLabel}\\n  />\\n</form>\",\"position\":{\"start\":{\"line\":275,\"column\":1,\"offset\":9458},\"end\":{\"line\":283,\"column\":4,\"offset\":9688},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label=\\\"a + img\\\"\",\"value\":\"<a aria-label={applyWithSeekButton.buttonLabel} href={applyWithSeekButton.url}>\\n  <img\\n    alt={applyWithSeekButton.buttonLabel}\\n    src={applyWithSeekButton.images.accent}\\n  />\\n</a>\",\"position\":{\"start\":{\"line\":285,\"column\":1,\"offset\":9690},\"end\":{\"line\":292,\"column\":4,\"offset\":9899},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label=\\\"button\\\"\",\"value\":\"<button\\n  aria-label={applyWithSeekButton.buttonLabel}\\n  onClick={() => {\\n    window.location.href = applyWithSeekButton.url;\\n  }}\\n  onKeyDown={(event) => {\\n    if (event.key === 'Enter' || event.key === ' ') {\\n      event.preventDefault();\\n      window.location.href = applyWithSeekButton.url;\\n    }\\n  }}\\n  role=\\\"link\\\"\\n  style={{ background: applyWithSeekButton.images.accent }}\\n/>\",\"position\":{\"start\":{\"line\":294,\"column\":1,\"offset\":9901},\"end\":{\"line\":309,\"column\":4,\"offset\":10309},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label": "\\\"form",
        "+": true,
        "input\\\"\",\"value\":\"<form": true,
        "action": "{applyWithSeekButton.url}",
        "method": "\\\"GET\\\">\\n",
        "": true,
        "<input\\n": true,
        "style": {},
        "background:": true,
        "applyWithSeekButton.images.accent": true,
        "}}\\n": true,
        "type": "\\\"submit\\\"\\n",
        "value": "{applyWithSeekButton.buttonLabel}\\n",
        "/>\\n</form>\",\"position\":{\"start\":{\"line\":275,\"column\":1,\"offset\":9458},\"end\":{\"line\":283,\"column\":4,\"offset\":9688},\"indent\":[1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label": "\\\"a",
        "img\\\"\",\"value\":\"<a": true,
        "aria-label": "{applyWithSeekButton.buttonLabel}\\n",
        "href": "{applyWithSeekButton.url}>\\n",
        "<img\\n": true,
        "alt": "{applyWithSeekButton.buttonLabel}\\n",
        "src": "{applyWithSeekButton.images.accent}\\n",
        "/>\\n</a>\",\"position\":{\"start\":{\"line\":285,\"column\":1,\"offset\":9690},\"end\":{\"line\":292,\"column\":4,\"offset\":9899},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsx\",\"meta\":\"label": "\\\"button\\\"\",\"value\":\"<button\\n",
        "onClick": "{()",
        "{\\n": true,
        "window.location.href": true,
        "applyWithSeekButton.url;\\n": true,
        "onKeyDown": "{(event)",
        "if": true,
        "(event.key": true,
        "'Enter'": true,
        "||": true,
        "event.key": true,
        "'": true,
        "')": true,
        "event.preventDefault();\\n": true,
        "}\\n": true,
        "role": "\\\"link\\\"\\n",
        "}}\\n/>\",\"position\":{\"start\":{\"line\":294,\"column\":1,\"offset\":9901},\"end\":{\"line\":309,\"column\":4,\"offset\":10309},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}}]": true
      }}>{`<form action={applyWithSeekButton.url} method="GET">
  <input
    style={{ background: applyWithSeekButton.images.accent }}
    type="submit"
    value={applyWithSeekButton.buttonLabel}
  />
</form>
`}</code></pre>
    <h3 {...{
      "id": "option-2-custom-button"
    }}>{`Option 2: Custom button`}</h3>
    <p>{`We recommend taking `}<a parentName="p" {...{
        "href": "#option-1-seek-button-recommended-"
      }}>{`SEEK’s branded button`}</a>{` as it presents consistent and recognisable branding to the candidate while reducing ongoing maintenance effort.
If your software has technical constraints that necessitate a custom button,
contact us to discuss further.`}</p>
    <p>{`Your implementation will be required to follow our brand guidelines,
and dynamically apply the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/buttonLabel"
      }}><inlineCode parentName="a">{`buttonLabel`}</inlineCode></a>{` and `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/url"
      }}><inlineCode parentName="a">{`url`}</inlineCode></a>{` to the custom element.`}</p>
    <p>{`Make sure to exclude the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/named-type/ApplyWithSeekButtonPayload/field/images"
      }}><inlineCode parentName="a">{`images`}</inlineCode></a>{` field from your query.
Your software should `}<a parentName="p" {...{
        "href": "/graphql/in-practice#selecting-fields"
      }}>{`only select the fields it uses`}</a>{` as a best practice to reduce the surface area of your integration.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scoobie-merged-code",
        "metastring": "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query ($input: ApplyWithSeekButtonInput!) {\\n  applyWithSeekButton(input: $input) {\\n    buttonLabel\\n    url\\n  }\\n}\",\"position\":{\"start\":{\"line\":327,\"column\":1,\"offset\":11171},\"end\":{\"line\":334,\"column\":4,\"offset\":11298},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n  \\\"input\\\": {\\n    // The HTTPS URL to redirect the candidate to after authorization.\\n    // Your software may embed dynamic state in the query string for CSRF protection.\\n    \\\"redirectUri\\\": \\\"https://example.com/position/123/apply\\\",\\n\\n    // The identifier for the SEEK hirer that is filling the position.\\n    // \\\"Partner Name on behalf of Hirer Name\\\" will be displayed on the authorization page.\\n    \\\"hirerId\\\": \\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n    // The seek-token received as part of the query string when the candidate arrived from SEEK.\\n    \\\"token\\\": \\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":336,\"column\":1,\"offset\":11300},\"end\":{\"line\":351,\"column\":4,\"offset\":11947},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n  \\\"applyWithSeekButton\\\": {\\n    \\\"buttonLabel\\\": \\\"Apply with SEEK\\\",\\n    \\\"url\\\": \\\"https://www.seek.com.au/awsk/authorize?id=00000000-0000-4000-8000-000000000000\\\"\\n  }\\n}\",\"position\":{\"start\":{\"line\":353,\"column\":1,\"offset\":11949},\"end\":{\"line\":360,\"column\":4,\"offset\":12125},\"indent\":[1,1,1,1,1,1,1]}}]",
        "[{\"type\":\"code\",\"lang\":\"graphql\",\"meta\":null,\"value\":\"query": true,
        "($input:": true,
        "ApplyWithSeekButtonInput!)": true,
        "{\\n": true,
        "": true,
        "applyWithSeekButton(input:": true,
        "$input)": true,
        "buttonLabel\\n": true,
        "url\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":327,\"column\":1,\"offset\":11171},\"end\":{\"line\":334,\"column\":4,\"offset\":11298},\"indent\":[1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"jsonc\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"input\\\":": true,
        "//": true,
        "The": true,
        "HTTPS": true,
        "URL": true,
        "to": true,
        "redirect": true,
        "the": true,
        "candidate": true,
        "after": true,
        "authorization.\\n": true,
        "Your": true,
        "software": true,
        "may": true,
        "embed": true,
        "dynamic": true,
        "state": true,
        "in": true,
        "query": true,
        "string": true,
        "for": true,
        "CSRF": true,
        "protection.\\n": true,
        "\\\"redirectUri\\\":": true,
        "\\\"https://example.com/position/123/apply\\\",\\n\\n": true,
        "identifier": true,
        "SEEK": true,
        "hirer": true,
        "that": true,
        "is": true,
        "filling": true,
        "position.\\n": true,
        "\\\"Partner": true,
        "Name": true,
        "on": true,
        "behalf": true,
        "of": true,
        "Hirer": true,
        "Name\\\"": true,
        "will": true,
        "be": true,
        "displayed": true,
        "authorization": true,
        "page.\\n": true,
        "\\\"hirerId\\\":": true,
        "\\\"seekAnzPublicTest:organization:seek:93WyyF1h\\\",\\n\\n": true,
        "seek-token": true,
        "received": true,
        "as": true,
        "part": true,
        "when": true,
        "arrived": true,
        "from": true,
        "SEEK.\\n": true,
        "\\\"token\\\":": true,
        "\\\"eyJqb2JJZCI6MCwiY2FuZGlkYXRlSWQiOjAsInNlc3Npb25JZCI6IiJ9\\\"\\n": true,
        "}\\n}\",\"position\":{\"start\":{\"line\":336,\"column\":1,\"offset\":11300},\"end\":{\"line\":351,\"column\":4,\"offset\":11947},\"indent\":[1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]}},{\"type\":\"code\",\"lang\":\"json\",\"meta\":null,\"value\":\"{\\n": true,
        "\\\"applyWithSeekButton\\\":": true,
        "\\\"buttonLabel\\\":": true,
        "\\\"Apply": true,
        "with": true,
        "SEEK\\\",\\n": true,
        "\\\"url\\\":": true,
        "\\\"https://www.seek.com.au/awsk/authorize?id": "00000000-0000-4000-8000-000000000000\\\"\\n",
        "}\\n}\",\"position\":{\"start\":{\"line\":353,\"column\":1,\"offset\":11949},\"end\":{\"line\":360,\"column\":4,\"offset\":12125},\"indent\":[1,1,1,1,1,1,1]}}]": true
      }}>{`query ($input: ApplyWithSeekButtonInput!) {
  applyWithSeekButton(input: $input) {
    buttonLabel
    url
  }
}
`}</code></pre>
    <h2 {...{
      "id": "content-localisation"
    }}>{`Content localisation`}</h2>
    <p>{`Your software should include the standard `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Accept-Language"
      }}><inlineCode parentName="a">{`Accept-Language`}</inlineCode></a>{` HTTP request header when it executes the `}<a parentName="p" {...{
        "href": "https://developer.seek.com/schema/#/query/applyWithSeekButton"
      }}><inlineCode parentName="a">{`applyWithSeekButton`}</inlineCode>{` query`}</a>{`.
We support multiple brands and languages across our markets,
so providing accurate country information and language preferences enables us to provide a more familiar experience for the candidate.`}</p>
    <p>{`See `}<a parentName="p" {...{
        "href": "/graphql/in-practice#content-localisation"
      }}>{`content localisation`}</a>{` for more information.`}</p>
    <h2 {...{
      "id": "security-considerations"
    }}>{`Security considerations`}</h2>
    <h3 {...{
      "id": "url-redirection"
    }}>{`URL redirection`}</h3>
    <p>{`The SEEK API implicitly trusts your software to provide a safe `}<inlineCode parentName="p">{`redirectUri`}</inlineCode>{`.
This is the same treatment given to `}<a parentName="p" {...{
        "href": "/use-cases/job-posting"
      }}>{`Job Posting`}</a>{` `}<inlineCode parentName="p">{`applicationUri`}</inlineCode>{`s provided for `}<a parentName="p" {...{
        "href": "/use-cases/job-posting/application-methods#linking-out-to-an-external-apply-form"
      }}>{`linking out`}</a>{`,
and avoids the maintenance burden of strict URL allowlists.`}</p>
    <p>{`Follow best practices around URL redirection and protect candidates from phishing attempts.
At a minimum,
your software must have server-side provisions to ensure the `}<inlineCode parentName="p">{`redirectUri`}</inlineCode>{` points to a web resource that your organization manages,
and prevent unprivileged clients from manipulating redirection behaviour.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://cheatsheetseries.owasp.org/cheatsheets/Unvalidated_Redirects_and_Forwards_Cheat_Sheet.html"
      }}>{`OWASP cheat sheet`}</a>{` for best practices,
and our `}<a parentName="p" {...{
        "href": "https://talent.seek.com.au/partners/terms-of-use/"
      }}>{`Terms of Use`}</a>{` for more information about your obligations.`}</p>
    <h3 {...{
      "id": "content-security-policy-compatibility"
    }}>{`Content Security Policy compatibility`}</h3>
    <p>{`This section is for integration partners who enforce strict content security policies in their web-based software.`}</p>
    <p><a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTTP/CSP"
      }}>{`Content Security Policy`}</a>{` (CSP) is a security feature that helps prevent various types of attacks, including Cross-Site Scripting (XSS) and data injection attacks.
Add the following directive to your CSP to load Apply with SEEK assets from the SEEK API:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`Content-Security-Policy: img-src 'self'
https://integration.seek.com/awsk/button-images/;
`}</code></pre>
    <p>{`This sample directive instructs the browser to only load images from your own domain (`}<inlineCode parentName="p">{`self`}</inlineCode>{`) and our `}<inlineCode parentName="p">{`images`}</inlineCode>{` directory.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;